
import * as PIXI from 'pixi.js';
import { Settings } from '../Settings';
import { SyncObject } from './syncObject';
import { applyRenderOrder, applyRenderPose, vehicleOffsets, setSpriteAnchor } from './renderLayout';

export class RippleVehicle {
	constructor(container) {
		this.container = container;
		this.currentVehicle = -1;
		this.initialVehicle = -1; // from introduction
		this.vehicle = null;
		this.mirror = false;
		this.spriteSheet = null;
		this.vehicleClasses = [ RippleBike, RippleDirtBike, null,  RippleSkateboard, RippleSkippyBall, RippleFoddyCan]
		this.state = {
		}
	}

	buildSprite(spriteSheet){
		this.spriteSheet = spriteSheet;

		if(this.currentVehicle === -1 && this.initialVehicle !== -1){
			this.selectVehicle(this.initialVehicle);
		} else if (this.vehicle && !this.vehicle.spriteBuild) {
			this.vehicle.buildSprite(spriteSheet);
			this.vehicle.spriteBuild = true;
		}
		this.updateRenderLayout();
	}

	updateRenderLayout(){
		const sprites = this.vehicle?.sprites || (this.vehicle?.containerSprite ? {ball: this.vehicle.containerSprite} : {});
		for (const [key, sprite] of Object.entries(sprites)) sprite.renderOffset = vehicleOffsets[this.currentVehicle]?.[key];
		applyRenderOrder(this.container, this.container.rippleCharacterClass?.sprites, sprites, this.currentVehicle);
	}

	selectVehicle(vehicle){
		if(this.currentVehicle !== vehicle){
			if(this.vehicle && this.vehicle.spriteBuild){
				this.vehicle.destroy();
			}
			if(this.vehicleClasses[vehicle-1]){
				this.vehicle = new this.vehicleClasses[vehicle-1](this.container);
				if(this.spriteSheet){
					this.vehicle.buildSprite(this.spriteSheet);
					this.vehicle.spriteBuild = true;
				}
			} else{
				this.vehicle = null;
			}
			this.currentVehicle = vehicle;
			this.lastPackageID = -1;
			this.updateRenderLayout();
		}
	}

	processServerData(parts, id, mirror, time, reset = false){
		if(!this.vehicle || parts.length !== this.vehicle.stateProcessList.length) return;

		this.mirror = mirror;

		this.vehicle.stateProcessList.forEach((state, i) => {
			const stateData = parts[i];

			if(reset || this.lastPackageID === -1){
				state.forcePosition(id, stateData.x, stateData.y, stateData.r, mirror, time);
			}else{
				state.updateServerPosition(id, stateData.x, stateData.y, stateData.r, time, mirror);
			}
		});
		this.lastPackageID = id;
	}

	interpolatePosition(time){
		if(!this.vehicle) return;
		if(this.spriteSheet && !this.vehicle.spriteBuild){
			this.vehicle.buildSprite(this.spriteSheet);
			this.vehicle.spriteBuild = true;
			this.updateRenderLayout();
		}

		this.vehicle.stateKeys.forEach(key => {
			this.vehicle.state[key].interpolatePosition(time, Settings.maxExtrapolation);
		});

		if(this.vehicle.customUpdate){
			this.vehicle.customUpdate();
		}

		this.vehicle.spriteProcessList?.forEach((sprite, i) => {
			const state = this.vehicle.stateProcessList[i];
			applyRenderPose(sprite, state, sprite.renderOffset);
			sprite.visible = !(state.destroyed);
		});

	}
}

class RippleBike {
	constructor(container){
		this.sprite = container;

		this.state = {
			body: new SyncObject(),
			wheelBack: new SyncObject(),
			wheelFront: new SyncObject(),
			pedals: new SyncObject(),
		}
		this.stateKeys = Object.keys(this.state);
		this.stateProcessList = [this.state.body, this.state.wheelBack, this.state.wheelFront, this.state.pedals];
	}

	buildSprite(spriteSheet){
		this.sprites = {
			body: new PIXI.Sprite(spriteSheet.textures['Bicycle_Body']),
			wheelBack: new PIXI.Sprite(spriteSheet.textures['Bicycle_WheelBack']),
			wheelFront: new PIXI.Sprite(spriteSheet.textures['Bicycle_WheelFront']),
			pedals: new PIXI.Sprite(spriteSheet.textures['Bicycle_Pedals']),
		}

		for(let obj in this.sprites){
			setSpriteAnchor(this.sprites[obj]);
		}


		this.sprite.addChild(this.sprites.body);
		this.sprite.addChild(this.sprites.wheelBack);
		this.sprite.addChild(this.sprites.wheelFront);
		this.sprite.addChild(this.sprites.pedals);

		this.spriteProcessList = [this.sprites.body, this.sprites.wheelBack, this.sprites.wheelFront, this.sprites.pedals];
	}

	destroy(){
		this.spriteProcessList.forEach(sprite => sprite.destroy());
	}
}

class RippleDirtBike {
	constructor(container){
		this.sprite = container;

		this.state = {
			body: new SyncObject(),
			wheelBack: new SyncObject(),
			wheelFront: new SyncObject(),
			frontSuspension: new SyncObject(),
			backSuspension: new SyncObject(),
		}
		this.stateKeys = Object.keys(this.state);
		this.stateProcessList = [this.state.body, this.state.wheelBack, this.state.wheelFront, this.state.frontSuspension, this.state.backSuspension];
	}

	buildSprite(spriteSheet){
		this.sprites = {
			body: new PIXI.Sprite(spriteSheet.textures['DirtBike_Body']),
			wheelBack: new PIXI.Sprite(spriteSheet.textures['DirtBike_WheelBack']),
			wheelFront: new PIXI.Sprite(spriteSheet.textures['DirtBike_WheelFront']),
			frontSuspension: new PIXI.Sprite(spriteSheet.textures['DirtBike_Axis']),
			backSuspension: new PIXI.Sprite(spriteSheet.textures['DirtBike_WheelSupport']),
		}

		for(let obj in this.sprites){
			setSpriteAnchor(this.sprites[obj]);
		}


		this.sprite.addChild(this.sprites.body);
		this.sprite.addChild(this.sprites.frontSuspension);
		this.sprite.addChild(this.sprites.backSuspension);
		this.sprite.addChild(this.sprites.wheelBack);
		this.sprite.addChild(this.sprites.wheelFront);

		this.spriteProcessList = [this.sprites.body, this.sprites.wheelBack, this.sprites.wheelFront, this.sprites.frontSuspension, this.sprites.backSuspension];
	}

	destroy(){
		this.spriteProcessList.forEach(sprite => sprite.destroy());
	}
}

class RippleSkateboard {
	constructor(container){
		this.sprite = container;

		this.state = {
			body: new SyncObject(),
			wheelBack: new SyncObject(),
			wheelFront: new SyncObject(),
		}
		this.stateKeys = Object.keys(this.state);
		this.stateProcessList = [this.state.body, this.state.wheelBack, this.state.wheelFront];
	}

	buildSprite(spriteSheet){
		this.sprites = {
			body: new PIXI.Sprite(spriteSheet.textures['SkateBoard_Board']),
			wheelBack: new PIXI.Sprite(spriteSheet.textures['Skateboard_Wheel']),
			wheelFront: new PIXI.Sprite(spriteSheet.textures['Skateboard_Wheel']),
		}

		for(let obj in this.sprites){
			setSpriteAnchor(this.sprites[obj]);
		}


		this.sprite.addChild(this.sprites.body);
		this.sprite.addChild(this.sprites.wheelBack);
		this.sprite.addChild(this.sprites.wheelFront);

		this.spriteProcessList = [this.sprites.body, this.sprites.wheelBack, this.sprites.wheelFront];
	}

	destroy(){
		this.spriteProcessList.forEach(sprite => sprite.destroy());
	}
}

class RippleSkippyBall {
	constructor(container){
		this.sprite = container;

		this.state = {
			base: new SyncObject(),
			p1: new SyncObject(),
			p2: new SyncObject(),
			p3: new SyncObject(),
			p4: new SyncObject(),
			p5: new SyncObject(),
			p6: new SyncObject(),
			p7: new SyncObject(),
			p8: new SyncObject(),
			p9: new SyncObject(),
			p10: new SyncObject(),
			p11: new SyncObject(),
			p12: new SyncObject(),
		}
		this.stateKeys = Object.keys(this.state);
		this.stateProcessList = [this.state.base, this.state.p1, this.state.p2, this.state.p3, this.state.p4, this.state.p5, this.state.p6, this.state.p7, this.state.p8, this.state.p9, this.state.p10, this.state.p11, this.state.p12];
	}

	destroy(){
		if(this.containerSprite){
			this.containerSprite.destroy();
			this.containerSprite = null;
		}
	}

	buildSprite(spriteSheet){
		this.buildMesh(spriteSheet);
	}

	buildMesh(spriteSheet){

		this.containerSprite = new PIXI.Container();

		const t = spriteSheet.textures['YogaBall'];
		const [meshVertices, meshUvs, meshIndices] = this.getMeshData();
		this.mesh = new PIXI.MeshSimple({texture: t, vertices: Float32Array.from(meshVertices), uvs: Float32Array.from(meshUvs), indices: Uint32Array.from(meshIndices)});
		this.mesh.autoUpdate = false;
		this.mesh.onRender = null;
		this.mesh.isMesh = true;

		this.containerSprite.addChild(this.mesh);

		this.sprite.addChild(this.containerSprite);

		this.handleFront = new PIXI.Sprite(spriteSheet.textures['YogaBall_Handle_Front']);
		this.handleBack = new PIXI.Sprite(spriteSheet.textures['YogaBallHandle_Back']);

		this.containerSprite.addChild(this.handleFront);
		this.containerSprite.addChildAt(this.handleBack, 0);
	}

	updateMesh(){
		const [vertices, uvs] = this.getMeshData();

		const positionBuffer = this.mesh.geometry.getBuffer('aPosition');
		const uvBuffer = this.mesh.geometry.getBuffer('aUV');
		positionBuffer.data = Float32Array.from(vertices);
		uvBuffer.data = Float32Array.from(uvs);
		positionBuffer.update();
		uvBuffer.update();
	}

	updateHandles(){
		this.handleFront.position.x = -20;
		this.handleBack.position.x = -6.5;
		const baseYOffset = this.wobbafett || 34;

		const dx = this.state.p1.x - this.state.base.x;
		const dy = this.state.p1.y - this.state.base.y;
		const l = Math.sqrt(dx * dx + dy * dy);
		const y = -l - baseYOffset;

		this.handleFront.position.y = y;
		this.handleBack.position.y = y;
	}


	getMeshData(){
		const radiusAr = [];
		const vertices = [0,0];
		const indices = [];

		const steps = 12;
		const step = 360 / steps;


		const rInc = 16;
		for(let i = 0; i < steps; i++) {


			const targetBodyIndex = (i + (this.offsetIndex || 3)) % steps + 1;

			const p = this.state[`p${targetBodyIndex}`];

			const dx = this.state.base.x - p.x;
			const dy = this.state.base.y - p.y;
			const l = Math.sqrt(dx * dx + dy * dy);
			radiusAr.push(l);

			const r = radiusAr[i] + rInc;
			vertices.push( Math.cos(Math.PI * i * step / 180)*r, Math.sin(Math.PI * i * step / 180)*r);
			indices.push(0, i + 1, i);
		}

		indices.push(0, 1, steps);


		const uvs = vertices.map((e, i) => {
			let r;
			if(i <= 1){
				r = 75;
			}else{
				const vindex = Math.floor((i-2) / 2);
				r = radiusAr[vindex] + rInc;
			}
			return 0.5 + e / r / 2;
		});

		return [vertices, uvs, indices]
	}

	customUpdate()	{
		if(!this.containerSprite) return;
		
		this.containerSprite.x = this.state.base.x;
		this.containerSprite.y = this.state.base.y;
		this.containerSprite.angle = this.state.base.r;
		this.containerSprite.visible = !(this.state.base.destroyed);

		this.updateMesh();
		this.updateHandles();
	}
}

class RippleFoddyCan {
	constructor(container){
		this.sprite = container;

		this.state = {
			body: new SyncObject(),
			hammer: new SyncObject(),
		}
		this.stateKeys = Object.keys(this.state);
		this.stateProcessList = [this.state.body, this.state.hammer];
	}

	buildSprite(spriteSheet){
		this.sprites = {
			body: new PIXI.Sprite(spriteSheet.textures['Pot']),
			hammer: new PIXI.Sprite(spriteSheet.textures['Hammer']),
		}

		for(let obj in this.sprites){
			setSpriteAnchor(this.sprites[obj]);
		}


		this.sprite.addChild(this.sprites.hammer);
		this.sprite.addChild(this.sprites.body);

		this.spriteProcessList = [this.sprites.body, this.sprites.hammer];
	}

	destroy(){
		this.spriteProcessList.forEach(sprite => sprite.destroy());
	}
}
